import React, { useState } from 'react';
import { DialogClose, DialogContent, DialogTitle, DialogDescription } from '../shared/Dialog';
import { OrderApi, OrderInvoice } from '@/lib/OrderApi';
import { MainButton } from '@/components/shared/MainButton';
import { RiFileTextLine } from '@remixicon/react';
import * as m from "@/paraglide/messages.js"

interface GenerateInvoiceFormData {
    name: string;
    address_line_1: string;
    address_line_2: string;
    address_line_3: string;
    business_name: string;
    purchase_order_number: string;
}

interface GenerateInvoiceModalProps {
    orderId: string;
    invoiceSequenceId: string;
    defaultName?: string;
    onSuccess: (invoice: OrderInvoice) => void;
}

const GenerateInvoiceModal: React.FC<GenerateInvoiceModalProps> = ({ orderId, invoiceSequenceId, defaultName, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [formData, setFormData] = useState<GenerateInvoiceFormData>({
        name: defaultName || '',
        address_line_1: '',
        address_line_2: '',
        address_line_3: '',
        business_name: '',
        purchase_order_number: '',
    });

    const handleInputChange = (field: keyof GenerateInvoiceFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        // Clear error for this field when user types
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string[]> = {};

        if (!formData.name || formData.name.length < 2) {
            newErrors.name = [m.invoice_form_name_required()];
        }
        if (!formData.address_line_1 || formData.address_line_1.length < 2) {
            newErrors.address_line_1 = [m.invoice_form_address_line_1_required()];
        }
        if (!formData.address_line_2 || formData.address_line_2.length < 2) {
            newErrors.address_line_2 = [m.invoice_form_address_line_2_required()];
        }
        if (formData.business_name && formData.business_name.length < 2) {
            newErrors.business_name = [m.invoice_form_business_name_invalid()];
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const orderApi = new OrderApi(import.meta.env.VITE_API_URL);
            const result = await orderApi.generateInvoice(orderId, invoiceSequenceId, {
                name: formData.name,
                address_line_1: formData.address_line_1,
                address_line_2: formData.address_line_2,
                address_line_3: formData.address_line_3 || undefined,
                business_name: formData.business_name || undefined,
                purchase_order_number: formData.purchase_order_number || undefined,
            });

            if (!result.success) {
                setErrors(result.errors);
                return;
            }

            onSuccess(result.invoice);
        } catch (error) {
            console.error('Failed to generate invoice:', error);
            setErrors({ general: [m.invoice_form_error_general()] });
        } finally {
            setLoading(false);
        }
    };

    const inputClassName = "mt-1 block w-full rounded-md border-0 py-2 px-3 shadow-sm ring-1 ring-inset ring-[var(--shop-borderColor)] placeholder:text-[var(--shop-textColor)] placeholder:opacity-40 focus:ring-2 focus:ring-inset focus:ring-[var(--shop-ctaButtonColor)] sm:text-sm bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]";
    const labelClassName = "block text-sm font-medium text-[var(--shop-textColor)]";
    const errorClassName = "mt-1 text-sm text-[var(--shop-productErrorMessageText)]";

    return (
        <DialogContent className="bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]">
            <DialogTitle className="text-center">{m.invoice_modal_title()}</DialogTitle>
            <DialogDescription className="text-center">
                {m.invoice_modal_description()}
            </DialogDescription>

            <div className="mt-4 space-y-4">
                {errors.general && (
                    <div className="p-3 rounded-md bg-[var(--shop-productErrorMessageBackground)] text-[var(--shop-productErrorMessageText)]">
                        {errors.general.map((error, i) => (
                            <p key={i}>{error}</p>
                        ))}
                    </div>
                )}

                <div>
                    <label htmlFor="name" className={labelClassName}>
                        {m.invoice_form_name()} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        className={inputClassName}
                        placeholder={m.invoice_form_name_placeholder()}
                    />
                    {errors.name && <p className={errorClassName}>{errors.name[0]}</p>}
                </div>

                <div>
                    <label htmlFor="business_name" className={labelClassName}>
                        {m.invoice_form_business_name()}
                    </label>
                    <input
                        type="text"
                        id="business_name"
                        value={formData.business_name}
                        onChange={handleInputChange('business_name')}
                        className={inputClassName}
                        placeholder={m.invoice_form_business_name_placeholder()}
                    />
                    {errors.business_name && <p className={errorClassName}>{errors.business_name[0]}</p>}
                </div>

                <div>
                    <label htmlFor="address_line_1" className={labelClassName}>
                        {m.invoice_form_address_line_1()} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="address_line_1"
                        value={formData.address_line_1}
                        onChange={handleInputChange('address_line_1')}
                        className={inputClassName}
                        placeholder={m.invoice_form_address_line_1_placeholder()}
                    />
                    {errors.address_line_1 && <p className={errorClassName}>{errors.address_line_1[0]}</p>}
                </div>

                <div>
                    <label htmlFor="address_line_2" className={labelClassName}>
                        {m.invoice_form_address_line_2()} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="address_line_2"
                        value={formData.address_line_2}
                        onChange={handleInputChange('address_line_2')}
                        className={inputClassName}
                        placeholder={m.invoice_form_address_line_2_placeholder()}
                    />
                    {errors.address_line_2 && <p className={errorClassName}>{errors.address_line_2[0]}</p>}
                </div>

                <div>
                    <label htmlFor="address_line_3" className={labelClassName}>
                        {m.invoice_form_address_line_3()}
                    </label>
                    <input
                        type="text"
                        id="address_line_3"
                        value={formData.address_line_3}
                        onChange={handleInputChange('address_line_3')}
                        className={inputClassName}
                        placeholder={m.invoice_form_address_line_3_placeholder()}
                    />
                    {errors.address_line_3 && <p className={errorClassName}>{errors.address_line_3[0]}</p>}
                </div>

                <div>
                    <label htmlFor="purchase_order_number" className={labelClassName}>
                        {m.invoice_form_purchase_order_number()}
                    </label>
                    <input
                        type="text"
                        id="purchase_order_number"
                        value={formData.purchase_order_number}
                        onChange={handleInputChange('purchase_order_number')}
                        className={inputClassName}
                        placeholder={m.invoice_form_purchase_order_number_placeholder()}
                    />
                    {errors.purchase_order_number && <p className={errorClassName}>{errors.purchase_order_number[0]}</p>}
                </div>

                <MainButton
                    onClick={handleSubmit}
                    disabled={loading}
                    loading={loading}
                    className="w-full mt-6"
                >
                    <div className="flex items-center justify-center gap-2">
                        {loading ? m.invoice_form_generating() : m.invoice_form_generate()}
                        <RiFileTextLine className="w-5 h-5" />
                    </div>
                </MainButton>

                <DialogClose asChild>
                    <button className="w-full p-2 text-center text-[var(--shop-textColor)] hover:opacity-70 transition-opacity">
                        {m.invoice_form_cancel()}
                    </button>
                </DialogClose>
            </div>
        </DialogContent>
    );
};

export default GenerateInvoiceModal;



